import React from 'react';
import { Typography, Box, Button } from '@mui/material';
import { Archive, MessageSquare, Stars, Briefcase, Users } from 'lucide-react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// Refined footer box with subtle gradient and shadows
const FooterBox = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: '16px',
    margin: '12px 8px 16px',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.05)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(42, 42, 46, 0.9)'
        : '#F2F2F7'),
    borderRadius: '12px',
    border: isWallpaperMode
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.06)'
        : 'none'),
    boxShadow: isWallpaperMode
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? 'none'
        : '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)'),
    backdropFilter: 'blur(20px)',
    maxWidth: '244px',
    transition: 'all 0.2s ease',
  };
});

const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
});

const IconStyle = {
  width: '18px',
  height: '18px',
  strokeWidth: 1.5, // Thinner stroke for Apple-like icons
};

// More Apple-like typography
const TitleTypography = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontSize: '15px',
    fontWeight: 600,
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffffff' : '#1d1d1f'),
    letterSpacing: '-0.01em',
  };
});

const DescriptionTypography = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontSize: '13px',
    fontWeight: 400,
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.7)'
      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(60, 60, 67, 0.7)'),
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    maxHeight: '2.6em',
    letterSpacing: '-0.01em',
  };
});

// More Apple-like button
const LoadMoreButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontSize: '13px',
    fontWeight: 500,
    color: isWallpaperMode
      ? '#FFFFFF'
      : (theme.palette.mode === 'dark' ? '#0A84FF' : '#007AFF'),
    textTransform: 'none',
    padding: '6px 12px',
    minWidth: 'auto',
    borderRadius: '14px',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark' ? 'rgba(10, 132, 255, 0.1)' : 'rgba(0, 122, 255, 0.1)'),
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.15)'
        : (theme.palette.mode === 'dark' ? 'rgba(10, 132, 255, 0.15)' : 'rgba(0, 122, 255, 0.15)'),
      color: isWallpaperMode
        ? '#FFFFFF'
        : (theme.palette.mode === 'dark' ? '#0A84FF' : '#007AFF'),
    },
    '&:disabled': {
      color: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.3)'
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'),
    },
    transition: 'all 0.1s ease',
  };
});

/**
 * ChatListFooter
 *
 * Muestra un texto diferente según la categoría seleccionada.
 *
 * @param {string}  category          -> Categoría actual (personal, work, team, custom, etc.)
 * @param {number}  chatsCount        -> Cantidad de chats ya listados
 * @param {boolean} hasNextPage       -> Indica si hay más chats para cargar
 * @param {boolean} isNextPageLoading -> Indica si se está cargando la siguiente página
 * @param {function} onLoadMore       -> Acción para cargar más chats
 * @param {function} onCreateNewChat  -> Acción para crear un nuevo chat
 */
const ChatListFooter = ({
  category,
  chatsCount,
  hasNextPage,
  isNextPageLoading,
  onLoadMore,
  onCreateNewChat,
}) => {
  const { t } = useTranslation();

  // Si no hay chats, mostrar botón de crear nuevo chat para TODAS las categorías
  if (chatsCount === 0 && onCreateNewChat) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '16px 16px',
      }}>
        <Button
          variant="contained"
          onClick={onCreateNewChat}
          sx={(theme) => {
            const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
            return {
              borderRadius: '10px',
              padding: '8px 16px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              backgroundColor: isWallpaperMode
                ? '#FFFFFF'
                : '#0385FF',
              color: isWallpaperMode
                ? '#000000'
                : '#FFFFFF',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.9)'
                  : '#0374e0',
                boxShadow: 'none',
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
              transition: 'all 0.2s ease',
            };
          }}
        >
          {t('chatListFooter.startNewChat')}
        </Button>
      </Box>
    );
  }

  // 1) Si la categoría es "personal"
  if (category === 'personal') {
    // (A) Sin chats
    if (chatsCount === 0) {
      return (
        <FooterBox>
          <SectionHeader>
            <MessageSquare style={IconStyle} color="#0A84FF" />
            <TitleTypography>{t('chatListFooter.startChatting')}</TitleTypography>
          </SectionHeader>
          <DescriptionTypography sx={{ marginBottom: '14px' }}>
            {t('chatListFooter.startChatDescription')}
          </DescriptionTypography>
          <LoadMoreButton
            variant="text"
            size="small"
            onClick={() => {
              if (!isNextPageLoading) onLoadMore();
            }}
            disabled={isNextPageLoading}
          >
            {isNextPageLoading ? t('chatListFooter.loading') : t('chatListFooter.loadMore')}
          </LoadMoreButton>
        </FooterBox>
      );
    }
    // (B) Entre 1 y 5 chats
    if (chatsCount <= 5) {
      return (
        <FooterBox>
          <SectionHeader>
            <Stars style={IconStyle} color="#FF9F0A" />
            <TitleTypography>{t('chatListFooter.gettingStarted')}</TitleTypography>
          </SectionHeader>
          <DescriptionTypography sx={{ marginBottom: '14px' }}>
            {t('chatListFooter.buildingCollection')}
          </DescriptionTypography>
          <LoadMoreButton
            variant="text"
            size="small"
            onClick={() => {
              if (!isNextPageLoading) onLoadMore();
            }}
            disabled={isNextPageLoading}
          >
            {isNextPageLoading ? t('chatListFooter.loading') : t('chatListFooter.loadMore')}
          </LoadMoreButton>
        </FooterBox>
      );
    }
    // (C) Más de 5 chats
    return (
      <FooterBox>
        <SectionHeader>
          <Archive style={IconStyle} color="#8E8E93" />
          <TitleTypography>{t('chatListFooter.olderChats')}</TitleTypography>
        </SectionHeader>
        <DescriptionTypography sx={{ marginBottom: '14px' }}>
          {t('chatListFooter.showingChats', { count: chatsCount })}
        </DescriptionTypography>
        <LoadMoreButton
          variant="text"
          size="small"
          onClick={() => {
            if (!isNextPageLoading) onLoadMore();
          }}
          disabled={isNextPageLoading}
        >
          {isNextPageLoading ? t('chatListFooter.loading') : t('chatListFooter.loadMore')}
        </LoadMoreButton>
      </FooterBox>
    );
  }

  // 2) Si la categoría es "work"
  if (category === 'work') {
    return (
      <FooterBox>
        <SectionHeader>
          <Briefcase style={IconStyle} color="#0A84FF" />
          <TitleTypography>{t('chatListFooter.workCategory')}</TitleTypography>
        </SectionHeader>
        <DescriptionTypography sx={{ marginBottom: '14px' }}>
          {chatsCount === 0
            ? t('chatListFooter.createFirstWork')
            : t('chatListFooter.professionalChats')}
        </DescriptionTypography>
        <LoadMoreButton
          variant="text"
          size="small"
          onClick={() => {
            if (!isNextPageLoading) onLoadMore();
          }}
          disabled={isNextPageLoading}
        >
          {isNextPageLoading ? t('chatListFooter.loading') : t('chatListFooter.loadMore')}
        </LoadMoreButton>
      </FooterBox>
    );
  }

  // 3) Si la categoría es "team"
  if (category === 'team') {
    return (
      <FooterBox>
        <SectionHeader>
          <Users style={IconStyle} color="#FF9F0A" />
          <TitleTypography>{t('chatListFooter.teamCategory')}</TitleTypography>
        </SectionHeader>
        <DescriptionTypography sx={{ marginBottom: '14px' }}>
          {chatsCount === 0
            ? t('chatListFooter.startTeam')
            : t('chatListFooter.teamChats')}
        </DescriptionTypography>
        <LoadMoreButton
          variant="text"
          size="small"
          onClick={() => {
            if (!isNextPageLoading) onLoadMore();
          }}
          disabled={isNextPageLoading}
        >
          {isNextPageLoading ? t('chatListFooter.loading') : t('chatListFooter.loadMore')}
        </LoadMoreButton>
      </FooterBox>
    );
  }

  // 4) Para categorías personalizadas o desconocidas
  return (
    <FooterBox>
      <SectionHeader>
        <Archive style={IconStyle} color="#8E8E93" />
        <TitleTypography>{t('chatListFooter.customCategory')}</TitleTypography>
      </SectionHeader>
      <DescriptionTypography sx={{ marginBottom: '14px' }}>
        {chatsCount === 0
          ? t('chatListFooter.createCustom')
          : t('chatListFooter.collectedHere', { category })}
      </DescriptionTypography>
      <LoadMoreButton
        variant="text"
        size="small"
        onClick={() => {
          if (!isNextPageLoading) onLoadMore();
        }}
        disabled={isNextPageLoading}
      >
        {isNextPageLoading ? t('chatListFooter.loading') : t('chatListFooter.loadMore')}
      </LoadMoreButton>
    </FooterBox>
  );
};

export default ChatListFooter;
